/**
 * TOCSidebar - Table of Contents drawer for DocumentViewPro
 * Shows document sections with navigation and optional sentiment indicators
 */

import React from 'react';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export interface TocEntry {
  title: string;
  page: number;
  level: number;
  ref_id?: number;
  sentiment?: 'bullish' | 'bearish' | 'neutral';
}

interface TOCSidebarProps {
  open: boolean;
  onClose: () => void;
  tocData: TocEntry[];
  currentPage: number;
  onNavigateToPage: (page: number) => void;
}

const SENTIMENT_COLORS: Record<string, string> = {
  bullish: '#4caf50',
  bearish: '#f44336',
  neutral: '#9e9e9e',
};

const TOCSidebar: React.FC<TOCSidebarProps> = ({
  open,
  onClose,
  tocData,
  currentPage,
  onNavigateToPage,
}) => {
  // Find which section we're currently in
  const currentSectionIdx = React.useMemo(() => {
    let lastIdx = 0;
    for (let i = 0; i < tocData.length; i++) {
      if (tocData[i].page + 1 <= currentPage) {
        lastIdx = i;
      } else {
        break;
      }
    }
    return lastIdx;
  }, [tocData, currentPage]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          bgcolor: '#1A2341',
          color: 'white',
          top: 64,
          height: 'calc(100% - 64px)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Table of Contents
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* TOC entries */}
      <List sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
        {tocData.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="rgba(255,255,255,0.5)">
              No table of contents available
            </Typography>
          </Box>
        ) : (
          tocData.map((entry, idx) => {
            const isCurrent = idx === currentSectionIdx;
            const indent = Math.min(entry.level, 3) * 16;

            return (
              <ListItemButton
                key={idx}
                onClick={() => onNavigateToPage(entry.page + 1)} // TOC pages are 0-indexed
                sx={{
                  pl: 2 + indent / 8,
                  py: 0.75,
                  bgcolor: isCurrent ? 'rgba(74,144,226,0.2)' : 'transparent',
                  borderLeft: entry.sentiment
                    ? `3px solid ${SENTIMENT_COLORS[entry.sentiment]}`
                    : isCurrent
                      ? '3px solid #4A90E2'
                      : '3px solid transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <ListItemText
                  primary={entry.title}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: {
                      color: isCurrent ? '#4A90E2' : 'rgba(255,255,255,0.85)',
                      fontWeight: isCurrent ? 600 : entry.level <= 1 ? 500 : 400,
                      fontSize: entry.level === 0 ? '0.85rem' : '0.8rem',
                      lineHeight: 1.3,
                    },
                  }}
                  secondary={`p. ${entry.page + 1}`}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' },
                  }}
                />
              </ListItemButton>
            );
          })
        )}
      </List>
    </Drawer>
  );
};

export default TOCSidebar;
